import { MIN_PASSWORD_LENGTH } from '@ofmos/shared/auth';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useAppClient } from '../../data/ClientProvider';
import { queryKeys } from '../../data/queryKeys';
import { Button, FormField, Input, Spinner } from '../../ui';
import { AuthLayout } from './AuthLayout';
import { authErrorMessage } from './authMessages';
import { DEFAULT_BUTTON } from './LoginPage';
import { PASSWORD_ERROR } from './RegisterPage';

/**
 * `/invite/:token` (accounts F1, D-F1.3): the second way into this installation, and the
 * only screen in the product where somebody with no account types into a form.
 *
 * IT READS THE INVITATION FIRST, so the person sees who invited them and at which address
 * before they choose a password - and somebody holding a spent link reads why rather than
 * filling in a form that is going to refuse it.
 *
 * ACCEPTING SIGNS THEM IN. The link plus the password they just chose proves both things a
 * login proves, so a second sign-in step would be the same form twice; the session cookie
 * arrives with the response and the app opens at Home.
 *
 * IT IS THE CREATE-ACCOUNT FORM, BUILT OF THE SAME PIECES (F4, ledger E-W4-46): the labelled
 * kit fields `RegisterForm` uses, its one sentence about a short password, and the kit's
 * tinted content-area default button (`DEFAULT_BUTTON`) rather than the glass capsule this
 * screen shipped with - two ways into this installation that look like two products is how a
 * person stops trusting either of them.
 */
export function AcceptInvitationPage() {
  const client = useAppClient();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { token = '' } = useParams<{ token: string }>();
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  const invitation = useQuery({
    queryKey: ['invitation', token],
    queryFn: () => client.auth.getInvitation(token),
    retry: false,
    staleTime: Number.POSITIVE_INFINITY,
  });

  const tooShort = password.length > 0 && password.length < MIN_PASSWORD_LENGTH;
  /** Unavailable until there is something to send, as on sign-in and on Create account (R8). */
  const ready = name.trim() !== '' && password !== '';

  const submit = (): void => {
    if (name.trim() === '' || password === '' || tooShort || pending) return;
    setPending(true);
    setError(undefined);
    client.auth.acceptInvitation(token, { name, password }).then(
      (user) => {
        setPending(false);
        /**
         * Clear FIRST, then write the session, exactly as `useLogin` does: whatever this
         * browser cached belonged to somebody else or to nobody.
         */
        queryClient.clear();
        queryClient.setQueryData(queryKeys.session(), user);
        void navigate('/', { replace: true });
      },
      (cause: unknown) => {
        setPending(false);
        setError(authErrorMessage(cause));
      },
    );
  };

  if (invitation.isPending) {
    return (
      <AuthLayout title="Create account" subtitle="Checking the invitation.">
        <div className="flex justify-center py-4" aria-busy="true">
          <Spinner label="Checking the invitation" />
        </div>
      </AuthLayout>
    );
  }

  if (invitation.isError) {
    return (
      <AuthLayout
        title="This invitation doesn't work any more."
        subtitle="Ask whoever invited you for a new link."
      >
        {/* The server's sentence: it already says whether the link expired, was used or was withdrawn. */}
        <p className="text-sm text-ink-muted">{authErrorMessage(invitation.error)}</p>
        <p className="text-2xs text-ink-subtle">
          <Link to="/login" className="text-accent">
            Sign in
          </Link>
        </p>
      </AuthLayout>
    );
  }

  const invitedBy = invitation.data.invitedBy;

  return (
    <AuthLayout
      title="Create account"
      subtitle={
        invitedBy === null
          ? `You were invited to OFMOS CRM as ${invitation.data.email}.`
          : `${invitedBy} invited you to OFMOS CRM as ${invitation.data.email}.`
      }
    >
      <form
        className="space-y-5"
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <FormField label="Name" hint="What colleagues see beside the records you touch.">
          {(props) => (
            <Input
              {...props}
              autoComplete="name"
              autoFocus
              value={name}
              onChange={(event) => setName(event.target.value)}
              disabled={pending}
            />
          )}
        </FormField>

        <FormField
          label="Password"
          hint={`At least ${MIN_PASSWORD_LENGTH} characters.`}
          /** R8's own sentence, and the one `RegisterForm` already shows for this rule. */
          error={tooShort ? PASSWORD_ERROR : error}
        >
          {(props) => (
            <Input
              {...props}
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              disabled={pending}
              invalid={tooShort || error !== undefined}
            />
          )}
        </FormField>

        <Button
          type="submit"
          variant="push"
          block
          style={DEFAULT_BUTTON}
          disabled={!ready}
          loading={pending}
        >
          Create account
        </Button>
      </form>

      <p className="text-2xs text-ink-subtle">
        Already have an account?{' '}
        <Link to="/login" className="text-accent">
          Sign in
        </Link>
      </p>
    </AuthLayout>
  );
}
